import { api } from "lib/graphql/api"
import { gql } from "@apollo/client"
import { mutate } from "lib/graphql/mutate"

// --- Profile mutations ---

export const createFentraProfile = api(async function createFentraProfile({ name } = {}) {
    return await mutate(
        gql`
            mutation createFentraProfile($name: String!) {
                createFentraProfile(name: $name) {
                    _id
                    name
                    hasBearer
                    scimBaseUrl
                    ssoCallbackUrl
                    ssoLoginUrl
                    ssoMetadataUrl
                    spEntityId
                }
            }
        `,
        { name },
        { returns: "createFentraProfile" }
    )
})

export const renameFentraProfile = api(async function renameFentraProfile({ id, name } = {}) {
    return await mutate(
        gql`
            mutation renameFentraProfile($id: ID!, $name: String!) {
                renameFentraProfile(id: $id, name: $name) {
                    _id
                    name
                }
            }
        `,
        { id, name },
        { returns: "renameFentraProfile" }
    )
})

export const deleteFentraProfile = api(async function deleteFentraProfile({ id } = {}) {
    return await mutate(
        gql`
            mutation deleteFentraProfile($id: ID!) {
                deleteFentraProfile(id: $id)
            }
        `,
        { id },
        { returns: "deleteFentraProfile" }
    )
})

export const updateFentraConfig = api(async function updateFentraConfig({
    id,
    scimBaseUrl,
    bearer,
    ssoCallbackUrl,
    groupPrefix,
} = {}) {
    return await mutate(
        gql`
            mutation updateFentraConfig(
                $id: ID!
                $scimBaseUrl: String
                $bearer: String
                $ssoCallbackUrl: String
                $groupPrefix: String
            ) {
                updateFentraConfig(
                    id: $id
                    scimBaseUrl: $scimBaseUrl
                    bearer: $bearer
                    ssoCallbackUrl: $ssoCallbackUrl
                    groupPrefix: $groupPrefix
                ) {
                    _id
                    hasBearer
                    scimBaseUrl
                    ssoCallbackUrl
                    groupPrefix
                }
            }
        `,
        { id, scimBaseUrl, bearer, ssoCallbackUrl, groupPrefix },
        { returns: "updateFentraConfig" }
    )
})

export const testFentraConnection = api(async function testFentraConnection({ profileId } = {}) {
    return await mutate(
        gql`
            mutation testFentraConnection($profileId: ID!) {
                testFentraConnection(profileId: $profileId) {
                    ok
                    error
                    scimStatus
                }
            }
        `,
        { profileId },
        { returns: "testFentraConnection" }
    )
})

// --- User mutations ---

export const createFentraUser = api(async function createFentraUser({
    profileId,
    userName,
    displayName,
    givenName,
    familyName,
    email,
    jobTitle,
} = {}) {
    return await mutate(
        gql`
            mutation createFentraUser(
                $profileId: ID!
                $userName: String!
                $displayName: String
                $givenName: String
                $familyName: String
                $email: String
                $jobTitle: String
            ) {
                createFentraUser(
                    profileId: $profileId
                    userName: $userName
                    displayName: $displayName
                    givenName: $givenName
                    familyName: $familyName
                    email: $email
                    jobTitle: $jobTitle
                ) {
                    ok
                    error
                    scimStatus
                    user {
                        _id
                        profileId
                        externalId
                        userName
                        email
                        displayName
                        givenName
                        familyName
                        jobTitle
                        active
                        provisionStatus
                    }
                }
            }
        `,
        { profileId, userName, displayName, givenName, familyName, email, jobTitle },
        { returns: "createFentraUser" }
    )
})

export const updateFentraUser = api(async function updateFentraUser({
    id,
    userName,
    displayName,
    givenName,
    familyName,
    email,
    jobTitle,
    active,
} = {}) {
    return await mutate(
        gql`
            mutation updateFentraUser(
                $id: ID!
                $userName: String
                $displayName: String
                $givenName: String
                $familyName: String
                $email: String
                $jobTitle: String
                $active: Boolean
            ) {
                updateFentraUser(
                    id: $id
                    userName: $userName
                    displayName: $displayName
                    givenName: $givenName
                    familyName: $familyName
                    email: $email
                    jobTitle: $jobTitle
                    active: $active
                ) {
                    ok
                    error
                    scimStatus
                    user {
                        _id
                        userName
                        email
                        displayName
                        givenName
                        familyName
                        jobTitle
                        active
                        provisionStatus
                    }
                }
            }
        `,
        { id, userName, displayName, givenName, familyName, email, jobTitle, active },
        { returns: "updateFentraUser" }
    )
})

export const deactivateFentraUser = api(async function deactivateFentraUser({ id } = {}) {
    return await mutate(
        gql`
            mutation deactivateFentraUser($id: ID!) {
                deactivateFentraUser(id: $id) {
                    ok
                    error
                    scimStatus
                    user {
                        _id
                        active
                        provisionStatus
                    }
                }
            }
        `,
        { id },
        { returns: "deactivateFentraUser" }
    )
})

export const reactivateFentraUser = api(async function reactivateFentraUser({ id } = {}) {
    return await mutate(
        gql`
            mutation reactivateFentraUser($id: ID!) {
                reactivateFentraUser(id: $id) {
                    ok
                    error
                    scimStatus
                    user {
                        _id
                        active
                        provisionStatus
                    }
                }
            }
        `,
        { id },
        { returns: "reactivateFentraUser" }
    )
})

export const deleteFentraUser = api(async function deleteFentraUser({ id } = {}) {
    return await mutate(
        gql`
            mutation deleteFentraUser($id: ID!) {
                deleteFentraUser(id: $id)
            }
        `,
        { id },
        { returns: "deleteFentraUser" }
    )
})

export const linkFentraUser = api(async function linkFentraUser({ id } = {}) {
    return await mutate(
        gql`
            mutation linkFentraUser($id: ID!) {
                linkFentraUser(id: $id) {
                    ok
                    error
                    scimStatus
                    user {
                        _id
                        provisionStatus
                        externalId
                    }
                }
            }
        `,
        { id },
        { returns: "linkFentraUser" }
    )
})

// --- Group mutations ---

export const createFentraGroup = api(async function createFentraGroup({ profileId, displayName, description } = {}) {
    return await mutate(
        gql`
            mutation createFentraGroup($profileId: ID!, $displayName: String!, $description: String) {
                createFentraGroup(profileId: $profileId, displayName: $displayName, description: $description) {
                    ok
                    error
                    scimStatus
                    group {
                        _id
                        profileId
                        externalId
                        displayName
                        description
                        memberExternalIds
                    }
                }
            }
        `,
        { profileId, displayName, description },
        { returns: "createFentraGroup" }
    )
})

export const updateFentraGroup = api(async function updateFentraGroup({ id, displayName, description } = {}) {
    return await mutate(
        gql`
            mutation updateFentraGroup($id: ID!, $displayName: String, $description: String) {
                updateFentraGroup(id: $id, displayName: $displayName, description: $description) {
                    ok
                    error
                    scimStatus
                    group {
                        _id
                        displayName
                        description
                        memberExternalIds
                    }
                }
            }
        `,
        { id, displayName, description },
        { returns: "updateFentraGroup" }
    )
})

export const addFentraGroupMember = api(async function addFentraGroupMember({ groupId, userExternalId } = {}) {
    return await mutate(
        gql`
            mutation addFentraGroupMember($groupId: ID!, $userExternalId: String!) {
                addFentraGroupMember(groupId: $groupId, userExternalId: $userExternalId) {
                    ok
                    error
                    scimStatus
                    group {
                        _id
                        memberExternalIds
                    }
                }
            }
        `,
        { groupId, userExternalId },
        { returns: "addFentraGroupMember" }
    )
})

export const removeFentraGroupMember = api(async function removeFentraGroupMember({ groupId, userExternalId } = {}) {
    return await mutate(
        gql`
            mutation removeFentraGroupMember($groupId: ID!, $userExternalId: String!) {
                removeFentraGroupMember(groupId: $groupId, userExternalId: $userExternalId) {
                    ok
                    error
                    scimStatus
                    group {
                        _id
                        memberExternalIds
                    }
                }
            }
        `,
        { groupId, userExternalId },
        { returns: "removeFentraGroupMember" }
    )
})

export const deleteFentraGroup = api(async function deleteFentraGroup({ id } = {}) {
    return await mutate(
        gql`
            mutation deleteFentraGroup($id: ID!) {
                deleteFentraGroup(id: $id)
            }
        `,
        { id },
        { returns: "deleteFentraGroup" }
    )
})

// --- Sync ---

export const syncFentraState = api(async function syncFentraState({ profileId } = {}) {
    return await mutate(
        gql`
            mutation syncFentraState($profileId: ID!) {
                syncFentraState(profileId: $profileId) {
                    usersOk
                    usersFailed
                    groupsOk
                    groupsFailed
                    errors
                }
            }
        `,
        { profileId },
        { returns: "syncFentraState" }
    )
})

// --- Log mutations ---

export const clearFentraLog = api(async function clearFentraLog({ profileId } = {}) {
    return await mutate(
        gql`
            mutation clearFentraLog($profileId: ID!) {
                clearFentraLog(profileId: $profileId)
            }
        `,
        { profileId },
        { returns: "clearFentraLog" }
    )
})
